import { useState } from 'react';
import type { Me } from '@kaba/shared';
import { signOut } from '../lib/auth';
import { ArrowLeft } from 'lucide-react';
import { api } from '../lib/api';
import { useMe } from '../lib/auth';
import { queryClient } from '../lib/board';
import { navigate } from '../lib/router';
import { toast } from '../lib/toast';
import { FormField, inputCls } from './AdminPage';
import { PasswordInput } from './ui/PasswordInput';
import { NotificationSettings } from './NotificationSettings';
import { ApiTokensSection, CalendarFeedPanel } from './Integrations';
import { AppSessionsSection, SecuritySection, useAccounts } from './Security';
import { AppearanceSection } from './AppearanceSection';
import { NavigationSection } from './NavigationSection';
import { AvatarEditor, ProfileFields } from './ProfileDetails';
import { Button, IconButton } from './ui/ui';
import { offlineEnabledFor, setOfflineEnabled } from '../lib/offline';

export function ProfilePage() {
  const { data: me } = useMe();
  const { data: accounts } = useAccounts();
  const hasPassword = !accounts || accounts.includes('credential');
  const [name, setName] = useState(me?.name ?? '');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [next2, setNext2] = useState('');
  if (!me) return null;
  if (me.isGuest) return <GuestProfile name={name} setName={setName} current={me.name} me={me} />;

  return (
    <main className="mx-auto flex min-h-full max-w-xl flex-col gap-8 px-4 pb-16">
      <header className="pt-safe flex items-center gap-2 py-3">
        <IconButton label="Zur Übersicht" onClick={() => navigate('/')}>
          <ArrowLeft size={20} />
        </IconButton>
        <h1 className="text-xl font-bold">Profil</h1>
      </header>

      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await api.post('/api/auth/update-user', { name: name.trim() });
            await queryClient.invalidateQueries({ queryKey: ['me'] });
            toast.info('Name gespeichert.');
          } catch (err) {
            toast.error((err as Error).message);
          }
        }}
      >
        <h2 className="text-lg font-semibold">Konto</h2>
        <AvatarEditor me={me} />
        <FormField label="Name" htmlFor="p-name">
          <input id="p-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} required />
        </FormField>
        <FormField label="E-Mail" htmlFor="p-email" hint="Die E-Mail-Adresse kann derzeit nicht geändert werden.">
          <input id="p-email" className={inputCls} value={me.email} readOnly disabled />
        </FormField>
        <div className="flex justify-end">
          <Button type="submit" variant="primary" disabled={!name.trim() || name.trim() === me.name}>
            Speichern
          </Button>
        </div>
      </form>

      <ProfileFields me={me} />

      <AppearanceSection me={me} />

      <NavigationSection me={me} />

      <OfflineSection me={me} />

      <SecuritySection />
      {!me.isGuest && <AppSessionsSection />}

      <NotificationSettings me={me} />

      <section className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold">Kalender abonnieren</h2>
        <CalendarFeedPanel boardId={null} />
      </section>

      <ApiTokensSection />

      {!hasPassword && (
        <p className="rounded-xl border border-[var(--line)] p-3 text-sm text-[var(--ink-soft)]">
          Dein Konto hat kein Passwort – du meldest dich über Single Sign-on oder einen Passkey an.
        </p>
      )}
      {hasPassword && (
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          if (next !== next2) return toast.error('Die neuen Passwörter stimmen nicht überein.');
          try {
            await api.post('/api/auth/change-password', { currentPassword: current, newPassword: next, revokeOtherSessions: true });
            setCurrent('');
            setNext('');
            setNext2('');
            toast.info('Passwort geändert. Andere Geräte wurden abgemeldet.');
          } catch (err) {
            const msg = (err as Error).message;
            toast.error(/invalid password/i.test(msg) ? 'Das aktuelle Passwort stimmt nicht.' : msg);
          }
        }}
      >
        <h2 className="text-lg font-semibold">Passwort ändern</h2>
        <FormField label="Aktuelles Passwort" htmlFor="p-cur">
          <PasswordInput id="p-cur" className={inputCls} value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required />
        </FormField>
        <FormField label="Neues Passwort" htmlFor="p-new" hint="Mindestens 8 Zeichen.">
          <PasswordInput id="p-new" className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" minLength={8} required />
        </FormField>
        <FormField label="Neues Passwort wiederholen" htmlFor="p-new2">
          <PasswordInput id="p-new2" className={inputCls} value={next2} onChange={(e) => setNext2(e.target.value)} autoComplete="new-password" minLength={8} required />
        </FormField>
        <div className="flex justify-end">
          <Button type="submit" variant="primary">
            Passwort ändern
          </Button>
        </div>
      </form>
      )}
    </main>
  );
}

/** Gäste: nur Name ändern und abmelden */
function GuestProfile({ name, setName, current, me }: { name: string; setName: (s: string) => void; current: string; me: Me }) {
  return (
    <main className="mx-auto flex min-h-full max-w-xl flex-col gap-6 px-4 pb-16">
      <header className="pt-safe flex items-center gap-2 py-3">
        <IconButton label="Zur Übersicht" onClick={() => navigate('/')}>
          <ArrowLeft size={20} />
        </IconButton>
        <h1 className="text-xl font-bold">Gastzugang</h1>
      </header>
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await api.post('/api/auth/update-user', { name: name.trim() });
            await queryClient.invalidateQueries();
            toast.info('Name gespeichert.');
          } catch (err) {
            toast.error((err as Error).message);
          }
        }}
      >
        <AvatarEditor me={me} />
        <FormField label="Dein Name" htmlFor="g-name" hint="So erscheinst du bei Änderungen und Kommentaren.">
          <input id="g-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} required maxLength={60} />
        </FormField>
        <div className="flex justify-end">
          <Button type="submit" variant="primary" disabled={!name.trim() || name.trim() === current}>
            Speichern
          </Button>
        </div>
      </form>
      <AppearanceSection me={me} />
      <div className="flex flex-col gap-3 rounded-xl border border-[var(--line)] p-4 text-sm">
        <p>
          Du nutzt KaBa als Gast – ohne Konto. Dieses Gerät bleibt angemeldet. Nach dem Abmelden kommst du über den Gastlink wieder hinein, dann als
          neuer Gast.
        </p>
        <p className="text-[var(--ink-soft)]">
          Hast du ein Konto auf dieser Instanz? Dann melde dich ab und öffne den Gastlink erneut – dort kannst du dich anmelden.
        </p>
        <Button className="self-start" onClick={() => void signOut()}>
          Abmelden
        </Button>
      </div>
    </main>
  );
}

/** Offline auf diesem Gerät (0.44): Stand zwischenspeichern; ausschalten löscht ihn sofort */
function OfflineSection({ me }: { me: Me }) {
  const [on, setOn] = useState(() => offlineEnabledFor(me.id));
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-lg font-semibold">Offline</h2>
      <label className="flex min-h-11 items-start gap-3 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 size-5 accent-[var(--color-accent)]"
          checked={on}
          onChange={async (e) => {
            const v = e.target.checked;
            setOn(v);
            await setOfflineEnabled(queryClient, me.id, v);
            toast.info(v ? 'Zuletzt geöffnete Boards sind jetzt auch offline verfügbar.' : 'Der Offline-Stand wurde von diesem Gerät gelöscht.');
          }}
        />
        <span>
          Zuletzt geöffnete Boards und Karten auf diesem Gerät für die Nutzung ohne Verbindung speichern
          <span className="block text-xs text-[var(--ink-soft)]">
            Gilt nur für dieses Gerät. Beim Abmelden wird alles gelöscht. Auf fremden oder geteilten Geräten besser ausschalten. Änderungen ohne Verbindung werden in jedem Fall bis zur Übertragung hier aufbewahrt.
          </span>
        </span>
      </label>
    </section>
  );
}
